import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { playAmbientMusic, stopAmbientMusic } from "../lib/chime";

const STORAGE_KEY = "luxstay-ambient-music";

/**
 * Subtle ambient music toggle. Stored in localStorage so the user's
 * preference persists across sessions. Browsers require a user gesture
 * before any audio is allowed, so the music only ever starts after the
 * user clicks this button — perfect for autoplay-policy compliance.
 */
export default function MusicToggle() {
  const [playing, setPlaying] = useState(false);

  // Sync with localStorage on mount — but DO NOT auto-play (autoplay policy).
  // We simply reflect the previous state as OFF, leaving a visual hint.
  useEffect(() => {
    // Listen for other components (e.g. logout) dispatching a force-stop.
    const onStop = () => { setPlaying(false); stopAmbientMusic(); };
    window.addEventListener("luxstay:stop-music", onStop);
    return () => window.removeEventListener("luxstay:stop-music", onStop);
  }, []);

  function toggle() {
    if (playing) {
      stopAmbientMusic();
      setPlaying(false);
      try { localStorage.setItem(STORAGE_KEY, "0"); } catch { /* ignore */ }
    } else {
      const ok = playAmbientMusic({ volume: 0.09 });
      if (ok) {
        setPlaying(true);
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      }
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={playing ? "Desligar música de fundo" : "Ligar música de fundo"}
      title={playing ? "Desligar música" : "Música ambiente"}
      className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors
        ${playing
          ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]"
          : "border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37]"}`}
      data-testid="music-toggle"
    >
      {playing ? <Volume2 size={15} /> : <VolumeX size={15} />}
      {playing && (
        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5" aria-hidden="true">
          <span className="block w-0.5 h-1.5 bg-[#d4af37] rounded-full lux-eq-bar" style={{ animationDelay: "0s" }} />
          <span className="block w-0.5 h-2.5 bg-[#d4af37] rounded-full lux-eq-bar" style={{ animationDelay: "0.12s" }} />
          <span className="block w-0.5 h-1.5 bg-[#d4af37] rounded-full lux-eq-bar" style={{ animationDelay: "0.24s" }} />
        </span>
      )}
    </button>
  );
}
